import React from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { updateSettings } from "../../lib/api";
import { ISiteSettingsForm } from "../../types/forms";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";

type cutoffDate = {
    ageLevel: string;
    date: string;
};

const DateSettings = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();
    const settings = useAppSelector(state => state.global.settings);

    const settingsToShow = [
        "majorybl_current_league_year",
        "majorybl_current_league_season",
        "majorybl_max_roster_size",
        "majorybl_season_start_date",
        "majorybl_season_end_date",
        "majorybl_playoff_eligibility_date",
        "majorybl_highschool_playoff_eligibility_date",
    ];

    const {
        register,
        control,
        setValue,
        getValues,
        watch,
        handleSubmit,
        formState,
        reset,
    } = useForm<Partial<ISiteSettingsForm>>({
        mode: "onChange",
        defaultValues: {
            date: {
                ...settingsToShow.reduce(
                    (acc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            if (setting.options) {
                                acc[settingName] = {
                                    value: setting.options.indexOf(
                                        setting.setting,
                                    ),
                                    label: setting.setting,
                                };
                            } else {
                                acc[settingName] = setting.setting;
                            }
                        }
                        return acc;
                    },
                    {} as Record<string, any>,
                ),
                ...settings
                    .find(s => s.settingName === "majorybl_roster_cutoff_dates")
                    ?.setting.reduce(
                        (acc: Record<string, any>, s: any) => {
                            acc[`majorybl_roster_cutoff_dates_${s.ageLevel}`] =
                                s.date;
                            return acc;
                        },
                        {} as Record<string, any>,
                    ),
            },
        },
    });

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            date: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] =
                            data[category][subfield];
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );

                reset(data);
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="dateSettingsContent">
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    {settings
                        .filter(s => settingsToShow.includes(s.settingName))
                        .map((s, idx) => {
                            watch(
                                `date.${s.settingName}`,
                                getValues(`date.${s.settingName}`),
                            );
                            return (
                                <Form.Group
                                    as={Col}
                                    xs={6}
                                    md={4}
                                    className="col"
                                    controlId={s.settingName}
                                    key={idx}
                                >
                                    <Form.Label>
                                        {s.displayTitle && s.displayTitle}
                                    </Form.Label>
                                    {s.options ? (
                                        <Controller
                                            name={`date.${s.settingName}`}
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isClearable={false}
                                                    isSearchable={false}
                                                    onChange={value => {
                                                        setValue(
                                                            `date.${s.settingName}`,
                                                            value,
                                                            {
                                                                shouldDirty:
                                                                    true,
                                                            },
                                                        );
                                                    }}
                                                    options={s.options?.map(
                                                        (o, idx) => ({
                                                            value: idx,
                                                            label: o,
                                                        }),
                                                    )}
                                                />
                                            )}
                                        />
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            {...register(
                                                `date.${s.settingName}`,
                                            )}
                                            onChange={e => {
                                                setValue(
                                                    `date.${s.settingName}`,
                                                    e.target.value.toString(),
                                                    { shouldDirty: true },
                                                );
                                            }}
                                        />
                                    )}
                                </Form.Group>
                            );
                        })}
                </Row>
                <Row>
                    <Col>
                        <Form.Label>Roster Cutoff Dates</Form.Label>
                        <Table className="rosterCutoffAges">
                            <thead>
                                <tr>
                                    <th>League Age</th>
                                    <th>Cutoff Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {settings
                                    .find(
                                        s =>
                                            s.settingName ===
                                            "majorybl_roster_cutoff_dates",
                                    )
                                    ?.setting.map(
                                        (s: cutoffDate, idx: number) => {
                                            const settingName = `majorybl_roster_cutoff_dates_${s.ageLevel}`;
                                            watch(
                                                `date.${settingName}`,
                                                getValues(
                                                    `date.${settingName}`,
                                                ),
                                            );
                                            return (
                                                <tr key={idx}>
                                                    <td
                                                        className="rosterCutoffAgeLevel"
                                                        width={"25%"}
                                                    >
                                                        {s.ageLevel}
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            defaultValue={
                                                                s.date
                                                            }
                                                            name={settingName}
                                                            onChange={e => {
                                                                setValue(
                                                                    `date.${settingName}`,
                                                                    e.target.value.toString(),
                                                                    {
                                                                        shouldDirty:
                                                                            true,
                                                                    },
                                                                );
                                                            }}
                                                        ></Form.Control>
                                                    </td>
                                                </tr>
                                            );
                                        },
                                    )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
                <Button variant="primary" type="submit">
                    Update Settings
                </Button>
            </Form>
        </section>
    );
};

export default DateSettings;
